import { styled } from '@mui/material';
import theme from '../../styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faCode } from '@fortawesome/free-solid-svg-icons';
import MediaTransferLogo from '../../assets/media-transfer-logo.svg';
import DemoRecorderLogo from '../../assets/demo-recorder-logo.svg';

const products = [
  {
    title: 'Media Transfer',
    tagline: 'macOS · para videógrafos',
    description:
      'Descarga tus tarjetas SD a un SSD externo con progreso en vivo, omisión de duplicados y verificación byte a byte. Pensada para cámaras Sony (A6700, ZV-E10) pero compatible con cualquier tarjeta.',
    platform: 'macOS · Apple Silicon',
    logo: MediaTransferLogo,
    downloadUrl: 'https://github.com/eddyvarelae/media-transfer/releases/latest/download/media-transfer.dmg',
    repoUrl: 'https://github.com/eddyvarelae/media-transfer',
    tag: 'Nuevo',
  },
  {
    title: 'Demo Recorder',
    tagline: 'macOS · demos de producto',
    description:
      'Graba tu pantalla con cámara en picture-in-picture y audio del micrófono en un solo archivo. Ventana 9:16 lista para Instagram Stories, cámara en rectángulo, cuadrado o círculo, y salidas opcionales de solo-pantalla y solo-cámara.',
    platform: 'macOS · Apple Silicon',
    logo: DemoRecorderLogo,
    downloadUrl: 'https://github.com/eddyvarelae/demo-recorder/releases/latest/download/demo-recorder.dmg',
    repoUrl: 'https://github.com/eddyvarelae/demo-recorder',
    tag: 'Nuevo',
  },
];

const ProductsSection = () => {
  return (
    <MainContainer id='productos'>
      <TextContainer>
        <H2>Productos</H2>
        <Line />
        <H1>Herramientas listas para descargar</H1>
        <Subtitle>
          Aplicaciones que construimos y publicamos. Listas para usar.
        </Subtitle>
      </TextContainer>
      <CardsContainer>
        {products.map((p) => (
          <Card key={p.title}>
            <Logo src={p.logo} alt={`${p.title} logo`} />
            <CardBody>
              <TitleRow>
                <H3>{p.title}</H3>
                {p.tag && <Tag>{p.tag}</Tag>}
              </TitleRow>
              <Tagline>{p.tagline}</Tagline>
              <Paragraph>{p.description}</Paragraph>
              <Platform>{p.platform}</Platform>
              <Actions>
                <PrimaryAction
                  href={p.downloadUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Descargar
                </PrimaryAction>
                {p.repoUrl && (
                  <SecondaryAction
                    href={p.repoUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FontAwesomeIcon icon={faCode} />
                    Código
                  </SecondaryAction>
                )}
              </Actions>
            </CardBody>
          </Card>
        ))}
      </CardsContainer>
    </MainContainer>
  );
};

const MainContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  backgroundColor: theme.color.cream,
  padding: '120px 100px',
  gap: 50,
  '@media (max-width: 768px)': {
    padding: '80px 20px',
  },
}));
const TextContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16,
  maxWidth: 720,
  textAlign: 'center',
}));
const CardsContainer = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
  gap: 24,
  width: '100%',
  maxWidth: 1100,
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
}));
const Card = styled('div')(() => ({
  display: 'flex',
  gap: 20,
  backgroundColor: theme.color.light,
  border: `1px solid ${theme.color.beige}`,
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 4px 20px rgba(0, 28, 54, 0.06)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 28px rgba(0, 28, 54, 0.12)',
  },
  '@media (max-width: 520px)': {
    flexDirection: 'column',
  },
}));
const Logo = styled('img')(() => ({
  width: 96,
  height: 96,
  borderRadius: 20,
  flexShrink: 0,
}));
const CardBody = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
}));
const TitleRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
}));
const H1 = styled('h1')(() => ({
  color: theme.color.primary,
  fontFamily: theme.fontFamily.primary,
  fontWeight: 1000,
  fontSize: 40,
  textAlign: 'center',
  margin: 0,
  letterSpacing: '0.02em',
}));
const H2 = styled('h2')(() => ({
  color: theme.color.accent,
  fontFamily: theme.fontFamily.primary,
  fontWeight: 600,
  fontSize: 20,
  textAlign: 'center',
  margin: 0,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
}));
const H3 = styled('h3')(() => ({
  color: theme.color.primary,
  fontFamily: theme.fontFamily.primary,
  fontWeight: 1000,
  fontSize: 24,
  margin: 0,
  letterSpacing: '0.03em',
}));
const Subtitle = styled('p')(() => ({
  color: theme.color.textSecondary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 16,
  margin: 0,
}));
const Tagline = styled('p')(() => ({
  color: theme.color.textSecondary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 13,
  margin: '2px 0 10px',
}));
const Paragraph = styled('p')(() => ({
  color: theme.color.textPrimary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 14,
  lineHeight: 1.5,
  margin: '0 0 12px',
}));
const Platform = styled('p')(() => ({
  color: theme.color.textSecondary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 12,
  margin: '0 0 14px',
  letterSpacing: '0.04em',
}));
const Tag = styled('span')(() => ({
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.color.primary,
  backgroundColor: theme.color.accent,
  padding: '3px 8px',
  borderRadius: 999,
}));
const Line = styled('div')(() => ({
  width: 120,
  borderBottom: `1px solid ${theme.color.accent}`,
}));
const Actions = styled('div')(() => ({
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
}));
const PrimaryAction = styled('a')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  backgroundColor: theme.color.primary,
  color: theme.color.light,
  fontFamily: theme.fontFamily.secondary,
  fontSize: 14,
  fontWeight: 600,
  padding: '10px 18px',
  borderRadius: 8,
  textDecoration: 'none',
  transition: 'background-color 0.15s',
  ':hover': {
    backgroundColor: '#0A2847',
  },
}));
const SecondaryAction = styled('a')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  backgroundColor: 'transparent',
  color: theme.color.primary,
  fontFamily: theme.fontFamily.secondary,
  fontSize: 14,
  fontWeight: 600,
  padding: '10px 18px',
  borderRadius: 8,
  textDecoration: 'none',
  border: `1px solid ${theme.color.primary}`,
  transition: 'background-color 0.15s',
  ':hover': {
    backgroundColor: theme.color.beige,
  },
}));

export default ProductsSection;
